import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { analytics, type RiskLimit } from "@/api/analytics";
import { Badge, Card, CardBody, CardHeader } from "@/components/ui";

function fmtPct(n: number | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

function fmtNumber(n: number | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function fmtMoney(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

function StatusDot({ status }: { status: RiskLimit["status"] }) {
  const tone =
    status === "ok"
      ? "good"
      : status === "warning"
      ? "warn"
      : status === "danger"
      ? "bad"
      : "neutral";
  return <Badge tone={tone as "good" | "warn" | "bad" | "neutral"}>{status}</Badge>;
}

export function AnalyticsPage() {
  const summary = useQuery({
    queryKey: ["analytics", "summary"],
    queryFn: () => analytics.summary(),
    refetchInterval: 60_000,
  });
  const equity = useQuery({
    queryKey: ["analytics", "equity-curve"],
    queryFn: () => analytics.equityCurve(),
    refetchInterval: 30_000,
  });
  const journal = useQuery({
    queryKey: ["analytics", "journal"],
    queryFn: () => analytics.journal(),
    refetchInterval: 30_000,
  });
  const risk = useQuery({
    queryKey: ["analytics", "risk"],
    queryFn: () => analytics.risk(),
    refetchInterval: 30_000,
  });

  const equityPoints = (equity.data?.data ?? []).map((p) => ({
    t: new Date(p.timestamp * 1000).toLocaleTimeString(),
    equity: p.equity,
  }));

  const monthlyPoints = (summary.data?.monthly_returns ?? []).map((m) => ({
    month: m.month,
    pnl: m.pnl,
  }));

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Analytics</h1>

      {/* Summary stat grid */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Performance summary</h2>
        </CardHeader>
        <CardBody>
          {summary.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : !summary.data ? (
            <p className="text-sm text-slate-500">No data yet.</p>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4 lg:grid-cols-6">
              <Stat label="Total return" value={fmtPct(summary.data.total_return_pct)} />
              <Stat label="Win rate" value={fmtPct(summary.data.win_rate)} />
              <Stat
                label="Sharpe"
                value={fmtNumber(summary.data.sharpe_ratio)}
                hint="annualized"
              />
              <Stat
                label="Sortino"
                value={fmtNumber(summary.data.sortino_ratio)}
                hint="annualized"
              />
              <Stat label="Calmar" value={fmtNumber(summary.data.calmar_ratio)} />
              <Stat
                label="Max drawdown"
                value={fmtPct(summary.data.max_drawdown_pct)}
              />
              <Stat
                label="Profit factor"
                value={fmtNumber(summary.data.profit_factor)}
              />
              <Stat
                label="Expectancy"
                value={fmtMoney(summary.data.expectancy)}
                hint="per trade"
              />
              <Stat label="Trades" value={String(summary.data.total_trades)} />
              <Stat label="Wins" value={String(summary.data.winning_trades)} />
              <Stat label="Losses" value={String(summary.data.losing_trades)} />
              <Stat
                label="Avg win"
                value={fmtMoney(summary.data.avg_win)}
                tone="good"
              />
              <Stat
                label="Avg loss"
                value={fmtMoney(summary.data.avg_loss)}
                tone="bad"
              />
              <Stat
                label="Largest win"
                value={fmtMoney(summary.data.largest_win)}
                tone="good"
              />
              <Stat
                label="Largest loss"
                value={fmtMoney(summary.data.largest_loss)}
                tone="bad"
              />
              <Stat
                label="Trading days"
                value={String(summary.data.trading_days)}
              />
            </dl>
          )}
        </CardBody>
      </Card>

      {/* Equity curve */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Equity curve</h2>
        </CardHeader>
        <CardBody>
          {equity.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : equityPoints.length === 0 ? (
            <p className="text-sm text-slate-500">No equity history yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={equityPoints}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-slate-200 dark:stroke-slate-800"
                />
                <XAxis dataKey="t" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis domain={["dataMin", "dataMax"]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="equity"
                  stroke="rgb(var(--brand-color))"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardBody>
      </Card>

      {/* Monthly P&L + asset breakdown side by side */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Monthly P&amp;L</h2>
          </CardHeader>
          <CardBody>
            {monthlyPoints.length === 0 ? (
              <p className="text-sm text-slate-500">No monthly history yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyPoints}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-slate-200 dark:stroke-slate-800"
                  />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="pnl">
                    {monthlyPoints.map((p, i) => (
                      <Cell
                        key={i}
                        fill={p.pnl >= 0 ? "rgb(16,185,129)" : "rgb(239,68,68)"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">By asset class</h2>
          </CardHeader>
          <CardBody className="overflow-x-auto p-0">
            {!summary.data || Object.keys(summary.data.by_asset_class).length === 0 ? (
              <p className="p-4 text-sm text-slate-500">No closed positions yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-left uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Class</th>
                    <th className="px-3 py-2 text-right">Trades</th>
                    <th className="px-3 py-2 text-right">Win rate</th>
                    <th className="px-3 py-2 text-right">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.data.by_asset_class).map(([ac, row]) => (
                    <tr
                      key={ac}
                      className="border-t border-slate-200 dark:border-slate-800"
                    >
                      <td className="px-3 py-2 font-medium">{ac}</td>
                      <td className="px-3 py-2 text-right tnum">{row.trades}</td>
                      <td className="px-3 py-2 text-right tnum">
                        {row.win_rate.toFixed(1)}%
                      </td>
                      <td
                        className={`px-3 py-2 text-right tnum ${row.pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}
                      >
                        {fmtMoney(row.pnl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Risk + journal */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Risk</h2>
          </CardHeader>
          <CardBody>
            {risk.isLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : !risk.data ? (
              <p className="text-sm text-slate-500">No risk data.</p>
            ) : (
              <>
                <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-3">
                  <Stat label="Equity" value={fmtMoney(risk.data.current_equity)} />
                  <Stat label="Peak" value={fmtMoney(risk.data.peak_equity)} />
                  <Stat label="Drawdown" value={fmtPct(risk.data.drawdown_pct)} />
                  <Stat label="Daily P&L" value={fmtMoney(risk.data.daily_pnl)} />
                  <Stat label="Heat" value={fmtPct(risk.data.portfolio_heat)} />
                  <Stat
                    label="Margin used"
                    value={fmtMoney(risk.data.margin_used)}
                  />
                  <Stat
                    label="Margin avail"
                    value={fmtMoney(risk.data.margin_available)}
                  />
                  <Stat
                    label="Circuit breaker"
                    value={risk.data.circuit_breaker}
                    tone={risk.data.circuit_breaker === "active" ? "good" : "bad"}
                  />
                </dl>
                {risk.data.limits && risk.data.limits.length > 0 && (
                  <table className="w-full text-2xs">
                    <thead className="text-left uppercase text-slate-500">
                      <tr>
                        <th className="py-1">Limit</th>
                        <th className="py-1 text-right">Current</th>
                        <th className="py-1 text-right">Max</th>
                        <th className="py-1 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {risk.data.limits.map((l, i) => (
                        <tr
                          key={i}
                          className="border-t border-slate-200 dark:border-slate-800"
                        >
                          <td className="py-1">{l.name}</td>
                          <td className="py-1 text-right tnum">{l.current}</td>
                          <td className="py-1 text-right tnum">{l.max}</td>
                          <td className="py-1 text-right">
                            <StatusDot status={l.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Journal (recent entries)</h2>
          </CardHeader>
          <CardBody className="max-h-80 overflow-y-auto">
            {journal.isLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : !journal.data || journal.data.entries.length === 0 ? (
              <p className="text-sm text-slate-500">No journal entries.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {journal.data.entries.slice(0, 50).map((e, i) => (
                  <li
                    key={i}
                    className="border-t border-slate-100 dark:border-slate-800 py-1"
                  >
                    <span className="text-slate-500">
                      {new Date(e.time).toLocaleTimeString()}{" "}
                    </span>
                    <span className="font-mono">[{e.type}]</span> {e.message ?? ""}
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "good" | "bad";
}) {
  const color =
    tone === "good"
      ? "text-emerald-500"
      : tone === "bad"
      ? "text-red-500"
      : "text-slate-900 dark:text-slate-100";
  return (
    <div>
      <dt className="text-xs uppercase text-slate-500">{label}</dt>
      <dd className={`tnum font-medium ${color}`}>
        {value}
        {hint && <span className="ml-1 text-2xs text-slate-400">{hint}</span>}
      </dd>
    </div>
  );
}
